import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { Typography } from 'src/components/Typography';

interface DreamcoatIntroModalProps {
  open: boolean;
  tryItOutLoading?: boolean;
  maybeLaterLoading?: boolean;
  onClose: () => void;
  onMaybeLater: () => void;
  onTryItOut: () => void;
}

/**
 * One-time introduction to the new Dreamcoat navigation, shown the
 * first time a staff member clicks "Try New Nav" (i.e. before `dreamcoatIntroSeen`
 * is set). "Try It Out" switches them over; "Maybe Later" just dismisses. Both
 * mark the intro as seen via the parent so it never re-fires; the close (X) is a
 * pure cancel that leaves the intro un-seen so it can reappear on a later click.
 */
export const DreamcoatIntroModal: React.FC<DreamcoatIntroModalProps> = ({
  open,
  tryItOutLoading,
  maybeLaterLoading,
  onClose,
  onMaybeLater,
  onTryItOut,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'dreamcoat.introModal',
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title')}
      maxWidth="sm"
      dividers
      actionsJustify="space-between"
      actions={[
        {
          variant: 'tertiary',
          onClick: onMaybeLater,
          loading: maybeLaterLoading,
          disabled: tryItOutLoading,
          label: t('maybeLater'),
        },
        {
          variant: 'prominent',
          color: 'primary',
          onClick: onTryItOut,
          loading: tryItOutLoading,
          disabled: maybeLaterLoading,
          endIcon: <Icon icon={faArrowRight} />,
          label: t('tryItOut'),
        },
      ]}
    >
      <Typography variant="body1" sx={{ mb: 2 }}>
        {t('body1')}
      </Typography>
      <Typography variant="body1">
        <strong>{t('dontWorry')}</strong> {t('body2')}
      </Typography>
    </Dialog>
  );
};
